import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mic, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fireBigConfetti } from "@/lib/confetti";

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PremiumSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | success | timeout | error

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          fireBigConfetti();
          refreshUser();
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
      } catch {
        setStatus("error");
        return;
      }
      attempts += 1;
      if (attempts >= MAX_POLL_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-20 text-center" data-testid="premium-success-page">
      {status === "polling" && (
        <div data-testid="premium-success-polling">
          <Loader2 className="w-10 h-10 text-indigo-400 mx-auto mb-4 animate-spin" />
          <p className="text-sm text-slate-400">Confirming your payment...</p>
        </div>
      )}
      {status === "success" && (
        <div data-testid="premium-success-confirmed">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-semibold text-white">You're Premium now!</h1>
          <p className="text-sm text-slate-400 mt-2 mb-6">Unlimited AI mock interviews, your Premium badge, and priority recruiter visibility are now active.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/interview" data-testid="premium-success-interview-cta" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-background font-semibold text-sm px-5 py-2.5 rounded-lg">
              <Mic className="w-4 h-4" /> Start a Mock Interview
            </Link>
            <Link to="/profile" data-testid="premium-success-profile-cta" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white font-semibold text-sm px-5 py-2.5 rounded-lg">
              <User className="w-4 h-4" /> View Your Portfolio
            </Link>
          </div>
        </div>
      )}
      {(status === "timeout" || status === "error") && (
        <div data-testid="premium-success-error">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-semibold text-white">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-sm text-slate-400 mt-2 mb-6">{status === "timeout" ? "Your payment is taking longer than expected. Check back in a minute." : "We couldn't confirm your payment. If you were charged, please contact support."}</p>
          <Link to="/premium" className="inline-block bg-white/5 hover:bg-white/10 text-white font-semibold text-sm px-5 py-2.5 rounded-lg">Back to Premium</Link>
        </div>
      )}
    </div>
  );
}
